import React from 'react';
import moment from 'moment';
import Input, { IProps as IInputProps } from './Input';
import {
  BROWSER_DATE_FORMAT,
  BROWSER_DATE_FORMAT_REGEX,
  datesEqual,
} from 'app/utils/date';
import { T } from 'app/components/localization';

export interface IProps extends Omit<IInputProps, 'onChange' | 'value'> {
  value: Date | null;
  onChange?: (value: Date | null) => unknown;
}

interface IState {
  display: string | null;
  lastDate: Date | null;
}

/**
 * This component implements an <input type="date" /> and transparently handles
 * converting the values between string and Date.
 *
 * We have to maintain some state so that the input handles temporary invalid
 * dates without completely erasing it because of the two-way binding.
 *
 * This shares a lot of code with the DateTime component in
 * app/components/forms but this only handles dates.
 */
export default class DateInput extends React.Component<IProps, IState> {
  public static getDerivedStateFromProps(props: IProps, state: IState) {
    const changed = !datesEqual(props.value, state.lastDate);
    if (changed && props.value) {
      // don't auto-update the input unless we have a valid value prop
      const formattedDate = moment.utc(props.value).format(BROWSER_DATE_FORMAT);
      return { display: formattedDate, lastDate: props.value };
    }
    return null;
  }

  public state = {
    display: null,
    lastDate: null,
  };

  public render() {
    const {
      value,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onChange,
      ...props
    } = this.props;
    const isInvalid = this.state.display !== '' && value === null;
    return (
      <T>{t =>
        <Input
          type="date"
          value={this.state.display}
          onChange={this.onChange}
          onFocus={this.onFocus}

          // the following are for browsers that don't support input type="date"
          // title shows in the tooltip "please follow the format"
          title={t('forms.date-input.title')}
          placeholder={t('forms.date-input.placeholder')}
          // if the date is invalid (like Feb 30) we make the pattern empty
          // to make the input invalid and not submit the form (and show the
          // please follow the format tooltip)
          pattern={isInvalid ? '^$' : BROWSER_DATE_FORMAT_REGEX}

          {...props}
        />
      }</T>
    );
  }

  private onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // Slightly improves an issue on iOS where the native input reverts back
    // to the default value when "clearing". This works only once per focus,
    // so it can't just be set once in componentDidMount.
    // eslint-disable-next-line no-param-reassign
    event.currentTarget.defaultValue = '';
  };

  private onChange = (value: string) => {
    const parsedDate = this.parseDate(value);
    const changed = !datesEqual(parsedDate, this.props.value);
    this.setState({ display: value }, () => {
      if (changed && this.props.onChange) {
        this.props.onChange(parsedDate);
      }
    });
  };

  private parseDate = (rawDate: string) => {
    let date = null;
    if (rawDate !== '') {
      // use strict parsing to avoid parsing partial input, this ensures
      // that the `value` prop is only set when we have a fully valid date
      date = moment.utc(rawDate, BROWSER_DATE_FORMAT, true);
    }
    return (date && date.isValid()) ? date.toDate() : null;
  };
}
